import { useEffect, useId, useState, type ComponentType, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  AtSign,
  Bell,
  Calendar,
  CalendarClock,
  Info,
  Pin,
  Mic,
  Palette,
  SmilePlus,
  Sparkles,
  UserPlus,
  Video,
  Volume2,
  X,
  Zap,
} from "lucide-react";

type GuideFeatureDef = {
  key: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  iconTint: string;
  title: string;
  text: string;
};

const TINT = {
  accent: "chat-guide-tint chat-guide-tint--accent",
  green: "chat-guide-tint chat-guide-tint--green",
  amber: "chat-guide-tint chat-guide-tint--amber",
  violet: "chat-guide-tint chat-guide-tint--violet",
  sky: "chat-guide-tint chat-guide-tint--sky",
  rose: "chat-guide-tint chat-guide-tint--rose",
} as const;

const FEATURES: GuideFeatureDef[] = [
  {
    key: "theme",
    icon: Palette,
    iconTint: TINT.violet,
    title: "Thème & fonds",
    text: "Clair, sombre, accent, et un fond par conversation — le Chat s’habille comme vous.",
  },
  {
    key: "sound",
    icon: Volume2,
    iconTint: TINT.sky,
    title: "Sons",
    text: "Message, appel, Auriz : chaque son se choisit, ou se coupe, depuis le profil.",
  },
  {
    key: "pin",
    icon: Pin,
    iconTint: TINT.accent,
    title: "Épingler",
    text: "Épinglez un fil en haut. Les autres remontent tout seuls dès qu’un message arrive — comme WhatsApp.",
  },
  {
    key: "push",
    icon: Bell,
    iconTint: TINT.amber,
    title: "Notifications",
    text: "Messages, mentions, réactions, visio, salle d’attente, rappels : le push suit même si Chat est fermé.",
  },
  {
    key: "visio",
    icon: Video,
    iconTint: TINT.green,
    title: "Visio & audio",
    text: "Appel direct, salle jusqu’à 4, partage d’écran, bascule d’appareil — sans quitter le fil.",
  },
  {
    key: "plan",
    icon: CalendarClock,
    iconTint: TINT.amber,
    title: "Planification",
    text: "Un créneau, un titre, les participants cochés : mail, agenda et rappel partent tout seuls.",
  },
  {
    key: "agenda",
    icon: Calendar,
    iconTint: TINT.sky,
    title: "Agenda branché",
    text: "La visio atterrit dans l’agenda Suite. Déplacer l’heure, c’est mettre à jour tout le monde.",
  },
  {
    key: "guest",
    icon: UserPlus,
    iconTint: TINT.rose,
    title: "Invités externes",
    text: "Un lien par e-mail, sans compte Suite. Ils attendent en salle jusqu’à ce que vous lanciez.",
  },
  {
    key: "auriz",
    icon: Sparkles,
    iconTint: TINT.violet,
    title: "Auriz",
    text: "Le clin d’œil façon MSN : un wizz dans le DM, animé, sans quota — pour dire « eh, je suis là ».",
  },
  {
    key: "anim",
    icon: SmilePlus,
    iconTint: TINT.rose,
    title: "Interactions animées",
    text: "Réactions qui pleuvent, effets d’envoi (Impact, Discret, Écho), appui long façon Messenger.",
  },
  {
    key: "mention",
    icon: AtSign,
    iconTint: TINT.accent,
    title: "Mentions",
    text: "Un @ dans le groupe et la personne est notifiée. Répondre, transférer, copier : tout y est.",
  },
  {
    key: "media",
    icon: Mic,
    iconTint: TINT.green,
    title: "Vocaux & fichiers",
    text: "Un vocal, une photo, un fichier : ça part dans le fil, comme un message — sans quitter la conversation.",
  },
  {
    key: "live",
    icon: Zap,
    iconTint: TINT.amber,
    title: "Connexion & présence",
    text: "En ligne, occupé, ne pas déranger, « en train d’écrire », lu : ça vit, ça ne recharge pas.",
  },
];

function GuideFeature({
  icon: Icon,
  iconTint,
  title,
  children,
}: {
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  iconTint: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <article className="flex gap-3 rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)]/70 p-3">
      <span
        className={`flex size-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset ring-black/5 ${iconTint}`}
        aria-hidden
      >
        <Icon className="size-4" strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold leading-snug text-[var(--chat-text)]">{title}</h3>
        <p className="mt-1 text-xs leading-relaxed text-[var(--chat-muted)]">{children}</p>
      </div>
    </article>
  );
}

function ChatGuideDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="chat-guide-overlay" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="chat-guide-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--chat-border)] px-4 py-3.5">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--chat-accent-strong)]">
              Aurélia Chat
            </p>
            <h2 id={titleId} className="mt-0.5 text-lg font-semibold sm:text-xl">
              Principes & fonctionnement
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)] hover:text-[var(--chat-text)]"
            aria-label="Fermer"
          >
            <X className="size-4" strokeWidth={2} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">
          <p className="mb-4 text-sm leading-relaxed text-[var(--chat-muted)]">
            <strong className="font-semibold text-[var(--chat-text)]">Aurélia Chat</strong> c’est la
            messagerie de la Suite : privée, de groupe ou en salle, avec visio, agenda, invités
            externes et ce petit côté vivant — sons, thèmes, animations, Auriz — sans quitter
            Aurélia.
          </p>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {FEATURES.map((f) => (
              <GuideFeature key={f.key} icon={f.icon} iconTint={f.iconTint} title={f.title}>
                {f.text}
              </GuideFeature>
            ))}
          </div>
        </div>

        <footer className="shrink-0 border-t border-[var(--chat-border)] px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-4 py-2 text-sm font-semibold hover:bg-[var(--chat-surface-elevated)]"
          >
            Fermer
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}

export function ChatGuideModal() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        title="Principes d’Aurélia Chat"
        aria-label="Principes d’Aurélia Chat"
        className="rounded-lg p-1.5 text-[var(--chat-muted)] hover:bg-[var(--chat-accent-soft)] hover:text-[var(--chat-accent-strong)]"
        onClick={() => setOpen(true)}
      >
        <Info className="size-4" strokeWidth={2} />
      </button>
      <ChatGuideDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
